<script setup lang="ts">
  import { computed, defineProps, defineEmits } from 'vue'
  import { BulkMediaItem, MediaCollection } from '@/types'
  import MultiSelect from '@/assets/MultiSelect.vue'

  const props = defineProps({
    item: {
      type: Object as () => BulkMediaItem,
      required: true,
    },
    collections: {
      type: Array as () => MediaCollection[],
      required: true,
    },
    hasParent: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits(['update', 'set-primary'])

  const name = computed({
    get: () => props.item.name,
    set: (val: string) => emit('update', { ...props.item, name: val }),
  })

  const alt = computed({
    get: () => props.item.alt,
    set: (val: string) => emit('update', { ...props.item, alt: val }),
  })

  const title = computed({
    get: () => props.item.title,
    set: (val: string) => emit('update', { ...props.item, title: val }),
  })

  const isFreeLicense = computed({
    get: () => props.item.is_free_license,
    set: (val: boolean) => emit('update', { ...props.item, is_free_license: val }),
  })

  const mediaCollections = computed({
    get: () => props.item.collections,
    set: (val: number[]) => emit('update', { ...props.item, collections: val }),
  })

  const collectionItems = computed(() =>
    props.collections.map(c => ({ label: c.name, value: c.id }))
  )

  const fileSrc = computed(() => URL.createObjectURL(props.item.file))

  function formatSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }
</script>

<template>
  <div class="ml-4 h-full flex flex-col">
    <!-- Preview -->
    <div class="mb-3 flex items-center gap-3">
      <div class="w-16 h-16 rounded overflow-hidden bg-white flex-shrink-0">
        <img :src="fileSrc" class="w-full h-full object-contain" :alt="item.name" />
      </div>
      <div class="min-w-0">
        <p class="text-sm font-medium truncate">{{ item.file.name }}</p>
        <p class="text-xs opacity-60">{{ formatSize(item.file.size) }}</p>
      </div>
    </div>

    <!-- Status -->
    <div v-if="item.status === 'done'" class="alert alert-success py-2 px-3 text-xs mb-3">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-4 w-4" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span>Erfolgreich hochgeladen</span>
    </div>
    <div v-if="item.status === 'error'" class="alert alert-error py-2 px-3 text-xs mb-3">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-4 w-4" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span>{{ item.error || 'Fehler beim Hochladen' }}</span>
    </div>
    <div v-if="item.status === 'uploading'" class="alert py-2 px-3 text-xs mb-3">
      <span class="loading loading-spinner loading-xs"></span>
      <span>Wird hochgeladen...</span>
    </div>

    <!-- Primary indicator (read-only, selection happens on tile) -->
    <div v-if="hasParent" class="mb-3">
      <div v-if="item.isPrimary" class="flex items-center gap-1.5 text-xs text-info bg-info/20 rounded px-2 py-1.5">
        <svg class="h-4 w-4 fill-info" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M10,17L5,12L6.41,10.58L10,14.17L17.59,6.58L19,8M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" /></svg>
        <span class="font-medium">Wird als Beitragsbild verwendet</span>
      </div>
      <div v-else class="text-xs opacity-50 px-2 py-1.5">
        Nicht als Beitragsbild ausgewählt
      </div>
    </div>

    <!-- Form fields -->
    <label class="form-control w-full">
      <div class="label">
        <span class="label-text text-xs">Name</span>
      </div>
      <input type="text" v-model="name" class="input input-bordered input-sm w-full" :disabled="item.status !== 'pending'" />
    </label>

    <label class="form-control w-full">
      <div class="label">
        <span class="label-text text-xs">Alternative Beschreibung (alt)</span>
      </div>
      <input type="text" v-model="alt" class="input input-bordered input-sm w-full" :disabled="item.status !== 'pending'" />
    </label>

    <label class="form-control w-full">
      <div class="label">
        <span class="label-text text-xs">Bildnachweis/Urheber (title)</span>
      </div>
      <input type="text" v-model="title" class="input input-bordered input-sm w-full" :disabled="item.status !== 'pending'" />
    </label>

    <div class="form-control mt-2">
      <label class="label cursor-pointer justify-start gap-2">
        <input type="checkbox" class="toggle toggle-sm" v-model="isFreeLicense" :disabled="item.status !== 'pending'" />
        <span class="label-text text-xs">Lizenzfrei</span>
      </label>
    </div>

    <div class="mb-2">
      <label class="label">
        <span class="label-text text-xs">Kategorien</span>
      </label>
      <MultiSelect v-model="mediaCollections" :items="collectionItems" label-property="label" value-property="value" size="sm" />
    </div>
  </div>
</template>
